/**
 * Severity of how long a PRO has been sitting with the LC:
 *   none     — under 12 hours (or no/invalid timestamp)
 *   warn     — 12 hours or more (orange)
 *   alert    — 1 day or more (red)
 *   critical — 2 days or more (blinking red, bold)
 */
export type TimeOnLcSeverity = 'none' | 'warn' | 'alert' | 'critical';

export function timeOnLcSeverity(
  isoTimestamp: string | null | undefined,
  now: Date = new Date()
): TimeOnLcSeverity {
  if (!isoTimestamp) return 'none';
  const sent = new Date(isoTimestamp);
  if (isNaN(sent.getTime())) return 'none';

  const hours = (now.getTime() - sent.getTime()) / 3600_000;
  if (hours >= 48) return 'critical';
  if (hours >= 24) return 'alert';
  if (hours >= 12) return 'warn';
  return 'none';
}

/**
 * Elapsed time since a timestamp as "3d 2h" / "16h 46m" / "45m". Shared by the
 * LC View's "Time on LC" and the Dispatch Planner's "Time on Board" so both
 * clocks read the same way. Missing, invalid or future timestamps show "-".
 */
export function formatElapsed(isoTimestamp: string | null | undefined, now: Date = new Date()): string {
  if (!isoTimestamp) return '-';
  const since = new Date(isoTimestamp);
  if (isNaN(since.getTime())) return '-';
  const diffMs = now.getTime() - since.getTime();
  if (diffMs < 0) return '-';

  const totalMinutes = Math.floor(diffMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Tailwind classes for each severity tier. */
export const TIME_ON_LC_CLASSES: Record<TimeOnLcSeverity, string> = {
  none: '',
  warn: 'text-orange-500 font-medium',
  alert: 'text-red-600 font-medium',
  critical: 'text-red-600 font-bold animate-pulse',
};
